import * as XLSX from 'xlsx';
import { canonicaliseGroupName } from '../../lib/groupCanonical';
import type {
  FormType,
  LocationDefaultMetaAdType,
} from '../../types/franchiseLocationProfile';
import type { ImportRow } from './importApi';

// Column aliases match the REAL Snap Master Sheet headers (spec §6.3), not
// schema-shaped names. Matching is case-insensitive and whitespace-collapsed;
// the first alias that exists in the sheet wins. When a column is missing,
// the field stays null / empty -- imports don't abort on optional fields.
// Partial because not every ParsedRow field is column-sourced -- fbAdAccountId
// is derived (URL-extracted or text-fallback via the fbAdAccountUrl column).
const COLUMN_ALIASES: Partial<Record<keyof ParsedRow, string[]>> = {
  franchiseSuburb: ['Club', 'Suburb', 'Club Suburb', 'Franchise Suburb'],
  state: ['State'],
  groupRaw: ['Group'],
  packageLabel: ['Package'],
  // FB Ad Account column feeds TWO fields: fbAdAccountUrl (the hyperlink
  // target) and fbAdAccountId (extracted from the URL, or fallback to text
  // when the cell has a bare numeric ID). We alias under fbAdAccountUrl
  // and handle the column specifically in the parser.
  fbAdAccountUrl: ['FB Ad Account', 'Facebook Ad Account'],
  googleAdsUrl: ['GA Ad Account'],
  // GA ID is the authoritative source of googleAdsCustomerId in the XXX-XXX-XXXX
  // format. GA Ad Account is a fallback if a sheet only has that column.
  googleAdsCustomerId: ['GA ID', 'Google Ads Customer ID', 'GA Ad Account'],
  // The client's own CRM id for the club (Snap: the GymSales company id).
  crmId: ['CRM ID', 'GymSales ID', 'Company ID', 'CRM Company ID'],
  gymInfo: ['Gym Info'],
  clientDriveUrl: ['Client Drive'],
  reportUrl: ['Report dashboard URL', 'Report URL', 'Report Link', 'Reporting URL'],
  metaMonthlyBudget: ['Facebook Budget', 'Meta Budget', 'Meta Monthly Budget'],
  googleMonthlyBudget: ['Google Budget', 'Google Monthly Budget'],
  offerLandingPage: ['Offer Landing Page'],
  saleLandingPage: ['Sale Landing Page'],
  locationLandingPage: ['Location Landing Page'],
};

// Fields whose text sometimes contains the template header text instead of
// real data (e.g. the cell literally reads "FB Ad Account" on rows the AM
// hasn't filled in). Treat those cells as null and surface a preview warning.
// Only applied when the cell has no hyperlink attached -- if the display
// text IS "FB Ad Account" but the cell has a hyperlink to business.facebook.com,
// the URL is the real data and we use it.
const PLACEHOLDER_VALUES: Partial<Record<keyof ParsedRow, string[]>> = {
  fbAdAccountUrl: ['FB Ad Account', 'Facebook Ad Account'],
  googleAdsUrl: ['GA Ad Account'],
  googleAdsCustomerId: ['GA ID', 'GA Ad Account', 'Google Ads Customer ID'],
  crmId: ['CRM ID', 'GymSales ID', 'Company ID', 'CRM Company ID'],
  clientDriveUrl: ['Client Drive'],
  reportUrl: ['Report dashboard URL', 'Report URL', 'Report Link', 'Reporting URL'],
};

// Columns that must exist in the sheet header for the import preview to
// succeed — used by buildFieldToHeader to flag missingColumns. Distinct
// from row-level required values: a missing column blocks the whole
// import, a missing per-row value just produces an empty/null field
// (and the row preview surfaces it as `(missing)` so the AM still sees
// the gap).
//
// Round 4 (Fix 2): offerLandingPage and saleLandingPage demoted to
// optional columns. Sheets without those headers parse silently with
// empty landing-page values. Row-level "(missing)" feedback in the
// import preview compensates — and the brand-level FP template is the
// real source of authored landing URLs anyway.
const REQUIRED_FIELDS: Array<keyof ParsedRow> = [
  'franchiseSuburb',
  'state',
  'groupRaw',
];

// Exported so the new MapColumnsStep + applyMappingAndBulkSet helper
// can construct rows under the same shape the existing import flow
// already consumes.
export type ParsedRow = {
  franchiseSuburb: string;
  state: string;
  groupRaw: string;
  packageLabel: string | null;
  fbAdAccountId: string | null;
  fbAdAccountUrl: string | null;
  googleAdsCustomerId: string | null;
  googleAdsUrl: string | null;
  crmId: string | null;
  gymInfo: string | null;
  clientDriveUrl: string | null;
  reportUrl: string | null;
  metaMonthlyBudget: number | null;
  googleMonthlyBudget: number | null;
  // Round-5 (null=inherit): all three landing URL fields are
  // nullable on parse. Empty cells / missing columns produce null,
  // which the rest of the pipeline carries through to storage so
  // the resolver inherits from the brand's landingPageTemplate.
  offerLandingPage: string | null;
  saleLandingPage: string | null;
  locationLandingPage: string | null;
  // Round-6 (Map columns + set defaults): bulk-set enum fields. The
  // legacy auto-alias parser leaves both null; the new
  // applyMappingAndBulkSet helper sets them from the user's Table-2
  // picks. toImportRow uses these when populated, falling back to
  // the historical defaults (Generic / null) otherwise so the
  // existing import path keeps working unchanged.
  defaultFormType: FormType | null;
  defaultMetaAdType: LocationDefaultMetaAdType;
};

export type ParseResult = {
  rows: ParsedRow[];
  warnings: string[];
  missingColumns: string[];
  headerMap: Record<string, string>;
};

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, ' ');
}

function cellToStringRaw(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

function parseMoneyCents(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[$,\s]/g, ''));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

// Pulls a Facebook Ads account ID out of a business.facebook.com URL. The
// sheet's hyperlinks land on URLs like
//   https://business.facebook.com/adsmanager/manage/accounts?act=1234567890
// Returns just the digits, or null if no `act=` param is found.
export function extractFbAdAccountId(url: string): string | null {
  const m = /[?&]act=(\d+)/.exec(url);
  return m ? m[1]! : null;
}

function isPlaceholderText(field: keyof ParsedRow, trimmed: string): boolean {
  const placeholders = PLACEHOLDER_VALUES[field];
  if (!placeholders) return false;
  const lower = trimmed.toLowerCase();
  return placeholders.some((p) => p.toLowerCase() === lower);
}

function buildFieldToHeader(rawHeaders: string[]): {
  fieldToHeader: Partial<Record<keyof ParsedRow, string>>;
  missing: string[];
} {
  const headerByNormalized = new Map<string, string>();
  for (const h of rawHeaders) headerByNormalized.set(normalizeHeader(h), h);

  const fieldToHeader: Partial<Record<keyof ParsedRow, string>> = {};
  const missing: string[] = [];
  for (const [field, aliases] of Object.entries(COLUMN_ALIASES) as Array<[
    keyof ParsedRow,
    string[],
  ]>) {
    if (!aliases) continue;
    let found: string | undefined;
    for (const alias of aliases) {
      const raw = headerByNormalized.get(normalizeHeader(alias));
      if (raw !== undefined) {
        found = raw;
        break;
      }
    }
    if (found !== undefined) {
      fieldToHeader[field] = found;
    } else if (REQUIRED_FIELDS.includes(field)) {
      missing.push(field);
    }
  }
  return { fieldToHeader, missing };
}

type Counts = {
  placeholders: Map<keyof ParsedRow, number>;
  hyperlinks: Map<keyof ParsedRow, number>;
};

function bump(map: Map<keyof ParsedRow, number>, field: keyof ParsedRow) {
  map.set(field, (map.get(field) ?? 0) + 1);
}

// Read a column that prefers a hyperlink URL over the text value. Text is the
// fallback (users sometimes paste a URL without making it a hyperlink). A
// placeholder text (matching the column header) becomes null.
function readUrlOrText(
  row: Record<string, unknown>,
  hl: Record<string, string | null> | undefined,
  field: keyof ParsedRow,
  fieldToHeader: Partial<Record<keyof ParsedRow, string>>,
  counts: Counts,
): string | null {
  const header = fieldToHeader[field];
  if (!header) return null;
  const link = hl?.[header];
  if (link) {
    bump(counts.hyperlinks, field);
    return link;
  }
  const trimmed = cellToStringRaw(row[header]);
  if (!trimmed) return null;
  if (isPlaceholderText(field, trimmed)) {
    bump(counts.placeholders, field);
    return null;
  }
  return trimmed;
}

// Read a plain text column with placeholder-detection for fields that have
// an entry in PLACEHOLDER_VALUES.
function readText(
  row: Record<string, unknown>,
  field: keyof ParsedRow,
  fieldToHeader: Partial<Record<keyof ParsedRow, string>>,
  counts: Counts,
): string {
  const header = fieldToHeader[field];
  if (!header) return '';
  const trimmed = cellToStringRaw(row[header]);
  if (!trimmed) return '';
  if (isPlaceholderText(field, trimmed)) {
    bump(counts.placeholders, field);
    return '';
  }
  return trimmed;
}

// Pure parser: given pre-extracted rows (values + hyperlinks) and the raw
// header list, produce ParsedRows + warnings. Exported so tests can skip the
// File -> workbook -> rows pipeline entirely.
//
// hyperlinksPerRow is optional and aligned index-wise with rawRows. When
// omitted (e.g. CSV imports or tests that don't care about hyperlinks), the
// parser treats every cell as text-only.
//
// Round-6: an optional `fieldToHeader` override lets the new "Map
// columns" step bypass the auto-alias scan and use the AM's explicit
// mapping instead. When provided, REQUIRED_FIELDS aren't checked
// against the alias list (the step's own validation gates that
// upstream); the missingColumns return value is empty.
export function parseRowsFromSheet(
  rawRows: Array<Record<string, unknown>>,
  rawHeaders: string[],
  hyperlinksPerRow?: Array<Record<string, string | null>>,
  options?: { fieldToHeader?: Partial<Record<keyof ParsedRow, string>> },
): ParseResult {
  const { fieldToHeader, missing } = options?.fieldToHeader
    ? { fieldToHeader: options.fieldToHeader, missing: [] as string[] }
    : buildFieldToHeader(rawHeaders);

  const headerMap: Record<string, string> = {};
  for (const [field, header] of Object.entries(fieldToHeader)) {
    if (header) headerMap[field] = header;
  }

  const counts: Counts = {
    placeholders: new Map(),
    hyperlinks: new Map(),
  };

  const rows: ParsedRow[] = [];
  const warnings: string[] = [];

  rawRows.forEach((r, i) => {
    const hl = hyperlinksPerRow?.[i];
    const suburb = readText(r, 'franchiseSuburb', fieldToHeader, counts);
    if (!suburb) {
      warnings.push(`Row ${i + 2}: missing Club (franchiseSuburb) -- skipping.`);
      return;
    }

    // FB Ad Account column is dual-purpose: prefers hyperlink URL into
    // fbAdAccountUrl; bare-text fallback populates fbAdAccountId when the text
    // isn't the placeholder "FB Ad Account".
    let fbUrl: string | null = null;
    let fbId: string | null = null;
    const fbHeader = fieldToHeader.fbAdAccountUrl;
    if (fbHeader) {
      const link = hl?.[fbHeader];
      if (link) {
        fbUrl = link;
        fbId = extractFbAdAccountId(link);
        bump(counts.hyperlinks, 'fbAdAccountUrl');
      } else {
        const trimmed = cellToStringRaw(r[fbHeader]);
        if (trimmed) {
          if (isPlaceholderText('fbAdAccountUrl', trimmed)) {
            bump(counts.placeholders, 'fbAdAccountUrl');
          } else {
            fbId = trimmed;
          }
        }
      }
    }

    const googleAdsUrl = readUrlOrText(r, hl, 'googleAdsUrl', fieldToHeader, counts);

    rows.push({
      franchiseSuburb: suburb,
      state: readText(r, 'state', fieldToHeader, counts),
      groupRaw: readText(r, 'groupRaw', fieldToHeader, counts),
      packageLabel: readText(r, 'packageLabel', fieldToHeader, counts) || null,
      fbAdAccountId: fbId,
      fbAdAccountUrl: fbUrl,
      googleAdsCustomerId: readText(r, 'googleAdsCustomerId', fieldToHeader, counts) || null,
      googleAdsUrl,
      crmId: readText(r, 'crmId', fieldToHeader, counts) || null,
      gymInfo: readText(r, 'gymInfo', fieldToHeader, counts) || null,
      clientDriveUrl: readUrlOrText(r, hl, 'clientDriveUrl', fieldToHeader, counts),
      reportUrl: readUrlOrText(r, hl, 'reportUrl', fieldToHeader, counts),
      metaMonthlyBudget: (() => {
        const h = fieldToHeader.metaMonthlyBudget;
        return h ? parseMoneyCents(r[h]) : null;
      })(),
      googleMonthlyBudget: (() => {
        const h = fieldToHeader.googleMonthlyBudget;
        return h ? parseMoneyCents(r[h]) : null;
      })(),
      // Round-5 (null=inherit): no `?? ''` coercion any more. Null
      // means "inherit from the brand's landingPageTemplate"; empty
      // strings would defeat that signal.
      offerLandingPage: readUrlOrText(r, hl, 'offerLandingPage', fieldToHeader, counts),
      saleLandingPage: readUrlOrText(r, hl, 'saleLandingPage', fieldToHeader, counts),
      locationLandingPage: readUrlOrText(r, hl, 'locationLandingPage', fieldToHeader, counts),
      // Round-6: applyMappingAndBulkSet overwrites these from the
      // AM's Table-2 picks. Parser-only path leaves both null; the
      // toImportRow translator falls back to historical defaults
      // (Generic / null) when null lands here.
      defaultFormType: null,
      defaultMetaAdType: null,
    });
  });

  // Summary warnings: one line per column with hits on either counter.
  for (const [field, count] of counts.hyperlinks) {
    const header = fieldToHeader[field] ?? field;
    warnings.push(
      `${count} row(s) had hyperlink URLs extracted from "${header.trim()}".`,
    );
  }
  for (const [field, count] of counts.placeholders) {
    const header = fieldToHeader[field] ?? field;
    warnings.push(
      `${count} row(s) had placeholder template text in "${header.trim()}" -- treated as null.`,
    );
  }

  return { rows, warnings, missingColumns: missing, headerMap };
}

// Walks the worksheet cell-by-cell so we can capture hyperlink targets
// alongside values. sheet_to_json throws away the cell's `l.Target` metadata,
// which is where the real URL lives when a cell displays "Client Drive" but
// has a hyperlink to drive.google.com attached.
// Exported so the new MapColumnsStep can render raw rows for the
// auto-match preview and applyMappingAndBulkSet can replay the
// per-cell readers against the AM's chosen mapping.
export type ExtractedRow = {
  values: Record<string, unknown>;
  hyperlinks: Record<string, string | null>;
};

export type RawParseResult = {
  headers: string[];
  rows: ExtractedRow[];
};

function extractWorkbookRows(sheet: XLSX.WorkSheet): {
  headers: string[];
  rows: ExtractedRow[];
} {
  const ref = sheet['!ref'];
  if (!ref) return { headers: [], rows: [] };
  const range = XLSX.utils.decode_range(ref);

  const headers: string[] = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    const addr = XLSX.utils.encode_cell({ r: range.s.r, c });
    const cell = sheet[addr] as XLSX.CellObject | undefined;
    headers.push(cell?.v !== undefined && cell.v !== null ? String(cell.v) : '');
  }

  const rows: ExtractedRow[] = [];
  for (let r = range.s.r + 1; r <= range.e.r; r++) {
    const values: Record<string, unknown> = {};
    const hyperlinks: Record<string, string | null> = {};
    let anyValue = false;
    for (let c = range.s.c; c <= range.e.c; c++) {
      const header = headers[c - range.s.c];
      if (!header) continue;
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = sheet[addr] as (XLSX.CellObject & { l?: { Target: string } }) | undefined;
      values[header] = cell?.v ?? null;
      hyperlinks[header] = cell?.l?.Target ?? null;
      if (cell?.v !== undefined && cell.v !== null && String(cell.v).trim() !== '') {
        anyValue = true;
      }
    }
    if (anyValue) rows.push({ values, hyperlinks });
  }

  return { headers, rows };
}

export async function parseXlsxFile(file: File): Promise<ParseResult> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellHTML: false });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) {
    return { rows: [], warnings: ['No sheets in workbook.'], missingColumns: [], headerMap: {} };
  }
  const sheet = wb.Sheets[sheetName]!;
  const { headers, rows } = extractWorkbookRows(sheet);
  if (rows.length === 0) {
    return { rows: [], warnings: ['Sheet is empty.'], missingColumns: [], headerMap: {} };
  }
  return parseRowsFromSheet(
    rows.map((r) => r.values),
    headers,
    rows.map((r) => r.hyperlinks),
  );
}

// Round-6: raw read mode for the new "Map columns" step. Returns the
// headers + per-row values + hyperlinks without applying the legacy
// COLUMN_ALIASES auto-match — the step uses these inputs to seed
// the user-driven mapping UI and replays the per-cell readers via
// applyMappingAndBulkSet once the AM has picked their mapping.
//
// Empty sheets return `{ headers: [], rows: [] }`; the call site
// surfaces the empty case to the AM.
export async function parseXlsxFileRaw(file: File): Promise<RawParseResult> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellHTML: false });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return { headers: [], rows: [] };
  const sheet = wb.Sheets[sheetName]!;
  return extractWorkbookRows(sheet);
}

// Bridge parsed sheet rows to the server's ImportRow shape. Fields not present
// in the sheet (franchiseSuburbShort, franchiseSuburbSlug, defaultFormType,
// clubOpenedAt) get spec §6.3 defaults. franchiseSuburbSlug is auto-generated
// server-side when null.
//
// Round-6: defaultFormType comes from ParsedRow when set (Table-2
// bulk-set picked a value); otherwise the historical default
// ('Generic') keeps the legacy import flow's behaviour. The Meta ad
// type default doesn't land here because the import server-side
// defaults it to null on every row — see importService.ts (the field
// flows in via the location profile schema, not ImportRow).
export function toImportRow(p: ParsedRow): ImportRow {
  return {
    groupCanonicalKey: canonicaliseGroupName(p.groupRaw),
    franchiseSuburb: p.franchiseSuburb,
    franchiseSuburbShort: null,
    franchiseSuburbSlug: null,
    state: p.state,
    metaMasterMonthlyBudget: p.metaMonthlyBudget,
    googleMasterMonthlyBudget: p.googleMonthlyBudget,
    offerLandingPage: p.offerLandingPage,
    saleLandingPage: p.saleLandingPage,
    locationLandingPage: p.locationLandingPage,
    defaultFormType: p.defaultFormType ?? 'Generic',
    // Round-6 bug fix: bulk-set defaultMetaAdType propagates here.
    // Without this, the ParsedRow's value (set by
    // applyMappingAndBulkSet from the AM's Table-2 pick) was
    // silently dropped on the way to ImportRow and the server
    // landed null on every imported location.
    defaultMetaAdType: p.defaultMetaAdType,
    gymInfo: p.gymInfo,
    package: p.packageLabel,
    clubOpenedAt: null,
    fbAdAccountId: p.fbAdAccountId,
    fbAdAccountUrl: p.fbAdAccountUrl,
    googleAdsCustomerId: p.googleAdsCustomerId,
    googleAdsUrl: p.googleAdsUrl,
    crmId: p.crmId,
    clientDriveUrl: p.clientDriveUrl,
    reportUrl: p.reportUrl,
  };
}
